import React, { createContext, useState, ReactNode, useContext } from 'react';
import { Snackbar as MuiSnackbar, IconButton, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '@mui/material/styles';
import { v4 as uuid } from 'uuid';

type SnackbarColor = 'info' | 'warning' | 'error' | 'success';

interface SnackbarMessage {
  id: string;
  message: string;
  color: SnackbarColor;
  onUndo?: () => void;
}

interface SnackbarContextType {
  showSnackbar: (message: string, color?: SnackbarColor, onUndo?: () => void) => void;
}

export const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

interface SnackbarProviderProps {
  children: ReactNode;
}

export function SnackbarProvider({ children }: SnackbarProviderProps) {
  const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);
  const theme = useTheme();

  const showSnackbar = (msg: string, color: SnackbarColor = 'info', onUndo?: () => void) => {
    const id = uuid();
    const toast = { id, message: msg, color, onUndo };
    setSnackbar(toast); 
  };

  const handleClose = () => {
    setSnackbar(null);
  };

  const handleUndo = (onUndo?: () => void) => () => {
    if (onUndo) onUndo();
    handleClose();
  };

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      {snackbar && (
        <MuiSnackbar
          key={snackbar.id}
          open
          autoHideDuration={3000}
          onClose={(_, reason) => {
            if (reason === 'timeout' || reason === 'clickaway') {
              handleClose();
            }
          }}
          message={snackbar.message}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          action={
            <React.Fragment>
              {snackbar.onUndo ? (
                <Button
                  size="small"
                  color="inherit"
                  onClick={handleUndo(snackbar.onUndo)}
                  sx={{ color: theme.palette[snackbar.color].main }}
                >
                  UNDO
                </Button>
              ) : (
                <IconButton
                  size="small"
                  aria-label="close"
                  onClick={handleClose}
                  sx={{ color: theme.palette[snackbar.color].main }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </React.Fragment>
          }
          sx={{
            mt: 6,
            mr: 1,
            '& .MuiSnackbarContent-root': {
              boxShadow: 0,
              background: theme.palette.background.paper,
              color: theme.palette.text.primary,
              borderRadius: 2,
              border: `1px solid ${theme.palette[snackbar.color].main}`,
            },
          }}
        />
      )}
    </SnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const context = useContext(SnackbarContext);
  if (context === undefined) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
}
